import {
  Card,
  Grid,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  Divider,
  Typography,
  Checkbox,
  ListItemText,
  Chip,
  Box
} from "@mui/material";
import WDBox from "../../../../MaterialComponents/WDBox";
import WDButton from "../../../../MaterialComponents/WDButton";
import WDTypography from "../../../../MaterialComponents/WDTypography";
import AppService from "../../../../Configurations/Appservice";

import { Formik, Form, Field } from "formik";
import { object, string } from "yup";
import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import {
  dismissLoader,
  showLoader,
  showSuccessMessage,
  updateScreenTitle,
} from "../../../../LayoutContainers/AppLayout/redux/AppSlice";
import SubTitle from "../../../../Components/SubTitle";
import ServicePartnerOnboardingSection from "../../../../Components/ServicePartnerOnboarding/ServicePartnerOnboardingSection";

const initialValues = {
  name: "",
  tradeLicenseNumber: "",
  contactPerson: "",
  email: "",
  contactNumber: "",
  address: "",
  organizationCategory: "",
  serviceTypes: [],
  active: true,
};

const validationSchema = object({
  name: string().required("Company Name is Mandatory"),
  tradeLicenseNumber: string(),
  contactPerson: string().required("Contact Person is Mandatory"),
  email: string().email("Invalid email format").required("Email is Mandatory"),
  contactNumber: string().required("Contact Number is Mandatory"),
  address: string().required("Address is Mandatory"),
  organizationCategory: string().required("Organization Category is Mandatory"),
});

const NewPartnerOrganisations = () => {
  // Check if current user is admin
  const isAdmin = () => {
    try {
      const principalInfoRaw = JSON.parse(localStorage.getItem("principalInfo"));
      const principalInfo = principalInfoRaw.principalInfo || principalInfoRaw;
      const isRootAdmin = principalInfo && principalInfo.roles && principalInfo.roles.includes("ROOT_ADMIN");
      return isRootAdmin;
    } catch (e) {
      return false;
    }
  };

  const dispatch = useDispatch();
  const [organizationCategories, setOrganizationCategories] = useState([]);
  const [serviceTypes, setServiceTypes] = useState([]);
  // Ref to the onboarding section so the org-create handler can persist
  // the partner's profit-share row right after the org is created.
  const onboardingRef = useRef(null);

  useEffect(() => {
    dispatch(updateScreenTitle("New Partner Organisation"));
    
    // Load organization categories from API
    AppService.get("api/organisations/categories")
      .then((response) => {
        setOrganizationCategories(response || []);
      })
      .catch((error) => {
        console.error("Failed to load organization categories", error);
      });

    // Load service types from API
    AppService.get("api/organisations/partner-service-types")
      .then((response) => {
        setServiceTypes(response || []);
      })
      .catch((error) => {
        console.error("Failed to load service types", error);
      });

    return () => dispatch(updateScreenTitle(""));
  }, []);

  const _onSubmit = async (values, { resetForm }) => {
    values.type = "PARTNER";
    if (!values.organizationCategory) {
      values.organizationCategory = null;
    }
    if (!values.serviceTypes || values.serviceTypes.length === 0) {
      values.serviceTypes = [];
    }

    // Run profit-share validation + low-share confirmation BEFORE creating
    // the org. If the user cancels the confirmation, abort without writing.
    if (onboardingRef.current?.isFilled()) {
      const ok = await onboardingRef.current.validate();
      if (!ok) return; // user cancelled — nothing has been written yet
    }

    dispatch(showLoader());
    try {
      const response = await AppService.post("v1/organisation", values);
      const newOrgId = response?.id || response?.data?.id;
      // Profit-share is required for service partners — assignTicketToUser
      // refuses tickets without it. Persist the onboarding row immediately so
      // the partner is usable straight after creation.
      if (newOrgId && onboardingRef.current?.isFilled()) {
        try {
          await onboardingRef.current.save(newOrgId);
        } catch (e) {
          console.error("Failed to save service partner onboarding", e);
          dispatch(showSuccessMessage("Partner created — but failed to save profit-share. Edit the partner to retry."));
          resetForm();
          onboardingRef.current?.reset();
          return;
        }
      }
      dispatch(showSuccessMessage("Partner Organisation Created Successfully"));
      resetForm();
      onboardingRef.current?.reset();
    } catch (error) {
      // Error already surfaced by AppService global handler
    } finally {
      dispatch(dismissLoader());
    }
  };

  return (
    <WDBox>
      <Card sx={{ padding: 3 }}>
        <Formik
          initialValues={initialValues}
          onSubmit={_onSubmit}
          validationSchema={validationSchema}
        >
          {({ errors, isValid, touched, dirty, values, setFieldValue }) => (
            <Form>
              {/* Section: Company Information */}
              <WDBox mb={2}>
                <SubTitle fontSize={14}>Company Information</SubTitle>
              </WDBox>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Field
                    name="name"
                    label="Company Name *"
                    fullWidth
                    as={TextField}
                    error={Boolean(errors.name) && Boolean(touched.name)}
                    helperText={Boolean(touched.name) && errors.name}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Field
                    name="tradeLicenseNumber"
                    label="Trade License Number"
                    fullWidth
                    as={TextField}
                    error={Boolean(errors.tradeLicenseNumber) && Boolean(touched.tradeLicenseNumber)}
                    helperText={Boolean(touched.tradeLicenseNumber) && errors.tradeLicenseNumber}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl
                    fullWidth
                    error={Boolean(errors.organizationCategory) && Boolean(touched.organizationCategory)}
                  >
                    <InputLabel>Organization Category *</InputLabel>
                    <Select
                      name="organizationCategory"
                      value={values.organizationCategory}
                      label="Organization Category *"
                      onChange={(e) => setFieldValue("organizationCategory", e.target.value)}
                    >
                      {organizationCategories.map((type) => (
                        <MenuItem key={type.code} value={type.code}>
                          {type.label}
                        </MenuItem>
                      ))}
                    </Select>
                    {Boolean(touched.organizationCategory) && errors.organizationCategory && (
                      <FormHelperText>{errors.organizationCategory}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Service Types</InputLabel>
                    <Select
                      multiple
                      name="serviceTypes"
                      value={values.serviceTypes || []}
                      label="Service Types"
                      onChange={(e) => setFieldValue("serviceTypes", e.target.value)}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((val) => (
                            <Chip key={val} label={val} size="small" />
                          ))}
                        </Box>
                      )}
                    >
                      {serviceTypes.map((type) => (
                        <MenuItem key={type} value={type}>
                          <Checkbox checked={(values.serviceTypes || []).indexOf(type) > -1} />
                          <ListItemText primary={type} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={8}>
                  <Field
                    name="address"
                    label="Address *"
                    fullWidth
                    as={TextField}
                    error={Boolean(errors.address) && Boolean(touched.address)}
                    helperText={Boolean(touched.address) && errors.address}
                  />
                </Grid>
              </Grid>

              {/* Section: Contact Details */}
              <Divider sx={{ my: 3 }} />
              <WDBox mb={2}>
                <SubTitle fontSize={14}>Contact Details</SubTitle>
              </WDBox>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Field
                    name="contactPerson"
                    label="Contact Person *"
                    fullWidth
                    as={TextField}
                    error={Boolean(errors.contactPerson) && Boolean(touched.contactPerson)}
                    helperText={Boolean(touched.contactPerson) && errors.contactPerson}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Field
                    name="email"
                    type="email"
                    label="Email *"
                    fullWidth
                    as={TextField}
                    error={Boolean(errors.email) && Boolean(touched.email)}
                    helperText={Boolean(touched.email) && errors.email}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Field
                    name="contactNumber"
                    label="Contact Number *"
                    fullWidth
                    as={TextField}
                    error={Boolean(errors.contactNumber) && Boolean(touched.contactNumber)}
                    helperText={Boolean(touched.contactNumber) && errors.contactNumber}
                  />
                </Grid>
                {isAdmin() && (
                  <Grid item xs={12} md={4}>
                    <Field name="active">
                      {({ field, form }) => (
                        <WDBox sx={{ display: 'flex', alignItems: 'center', gap: 1, pt: 1 }}>
                          <Typography variant="body2" color="text.secondary">Status:</Typography>
                          <input
                            type="checkbox"
                            checked={field.value}
                            onChange={() => form.setFieldValue("active", !field.value)}
                          />
                          <Typography variant="body2">
                            {field.value ? "Active" : "Inactive"}
                          </Typography>
                        </WDBox>
                      )}
                    </Field>
                  </Grid>
                )}
              </Grid>

              <Divider sx={{ my: 3 }} />
              <ServicePartnerOnboardingSection ref={onboardingRef} />

              <WDBox display="flex" justifyContent="flex-end" pt={3}>
                <WDButton variant="gradient" disabled={!dirty || !isValid} type="submit">
                  Create Organisation
                </WDButton>
              </WDBox>
            </Form>
          )}
        </Formik>
      </Card>
    </WDBox>
  );
};

export default NewPartnerOrganisations;
